import { CartAddEvent, ThemeEvents } from '@theme/events';

const root = document.querySelector('[data-klr-hero]');

if (root) {
  document.body.classList.add('klr-has-sticky');

  const variants = JSON.parse(root.querySelector('[data-klr-variants]')?.textContent || '[]');
  const themes = JSON.parse(root.querySelector('[data-klr-themes]')?.textContent || '[]');

  // ---- (disabled 7.10.2026 by Ido: site stays gold; kept for reuse) site recolors with the selected product color (accents + every tmg image on the page)
  // Images follow a filename convention: <base>.png is the main color, <base>--<slug>.png is that color.
  const IMG_RE = /(\/files\/)((?:tmg2?|tmg-pdp)-[a-z0-9-]+?)(?:--[a-z0-9]+)?(\.(?:png|jpg|jpeg|webp))/gi;
  const swapUrl = (url, slug) => url.replace(IMG_RE, (m, a, base, ext) => a + base + (slug ? '--' + slug : '') + ext);
  const applyTheme = (name) => {
    const t = themes.find((x) => x.name === name);
    if (!t) return;
    const rs = document.documentElement.style;
    rs.setProperty('--klr-accent', t.accent);
    rs.setProperty('--klr-accent-2', t.accent2);
    rs.setProperty('--klr-soft', t.soft);
    const slug = t.images ? t.slug || '' : '';
    document.querySelectorAll('.klr img').forEach((img) => {
      if (img.classList.contains('klr-main')) return;
      ['src', 'srcset'].forEach((attr) => {
        const v = img.getAttribute(attr);
        if (v && v.includes('/files/')) img.setAttribute(attr, swapUrl(v, slug));
      });
    });
    root.querySelectorAll('.thumb[data-src]').forEach((t2) => (t2.dataset.src = swapUrl(t2.dataset.src, slug)));
  };
  const bundles = [...root.querySelectorAll('.bundle')];
  const swatches = [...root.querySelectorAll('.swatch')];
  const prices = root.querySelectorAll('[data-klr-price]');
  const colorName = root.querySelector('[data-klr-color-name]');
  const colorShort = root.querySelector('[data-klr-color-short]');
  const buttons = root.querySelectorAll('[data-klr-atc]');
  const errorLine = root.querySelector('[data-klr-error]');

  const ils = (cents) => '₪' + Math.round(cents / 100).toLocaleString('en-US');
  const state = {
    color: (swatches.find((s) => s.classList.contains('on')) || swatches[0])?.dataset.color || variants[0]?.color,
    pack: (bundles.find((b) => b.classList.contains('on')) || bundles[0])?.dataset.pack || variants[0]?.pack,
  };
  // bundles are the same variant in quantity 1/2/3 (bundle price + qty live on the .bundle element; the discount is an automatic discount in Shopify)
  const packEl = () => bundles.find((b) => b.dataset.pack === state.pack) || bundles[0];
  const packQty = () => Number(packEl()?.dataset.qty || 1);
  const packPrice = (v) => (packEl()?.dataset.price ? Number(packEl().dataset.price) : v.price);
  const current = () => variants.find((v) => v.color === state.color && (v.pack == null || !packEl()?.dataset.qty || v.pack === state.pack)) || variants.find((v) => v.color === state.color) || variants[0];

  // ---- gallery carousel (scroll-snap; JS scrolls only the track, RTL-aware)
  const track = root.querySelector('[data-klr-track]');
  const slides = [...root.querySelectorAll('.klr-slide')];
  const dots = [...root.querySelectorAll('.klr-dots span')];
  const thumbs = [...root.querySelectorAll('.thumb[data-index]')];
  let cur = 0;
  const markActive = (i) => {
    cur = i;
    dots.forEach((d, k) => d.classList.toggle('on', k === i));
    thumbs.forEach((t, k) => t.classList.toggle('on', k === i));
    root.querySelectorAll('.klr-car-btn').forEach((b) => (b.disabled = Number(b.dataset.dir) < 0 ? i === 0 : i === slides.length - 1));
    const t = thumbs[i];
    if (t && t.parentElement) {
      const row = t.parentElement;
      const tl = t.offsetLeft, tw = t.offsetWidth;
      const rr = row.getBoundingClientRect(), tr = t.getBoundingClientRect();
      if (tr.left < rr.left || tr.right > rr.right) row.scrollBy({ left: tr.left < rr.left ? tr.left - rr.left - 8 : tr.right - rr.right + 8, behavior: 'smooth' });
    }
  };
  const goTo = (i) => {
    if (!slides.length) return;
    i = Math.max(0, Math.min(slides.length - 1, i)); // no loop: stops at the first and last image
    // scroll only the track (never the page); RTL tracks use negative scrollLeft
    const rtl = getComputedStyle(track).direction === 'rtl';
    track.scrollTo({ left: (rtl ? -1 : 1) * i * track.clientWidth, behavior: 'smooth' });
    markActive(i);
  };
  if (track && slides.length) {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting && e.intersectionRatio > 0.6) markActive(Number(e.target.dataset.index)); }),
      { root: track, threshold: [0.6] }
    );
    slides.forEach((sl) => io.observe(sl));
    root.querySelectorAll('.klr-car-btn').forEach((btn) => btn.addEventListener('click', () => goTo(cur + Number(btn.dataset.dir))));
    thumbs.forEach((t) => t.addEventListener('click', () => goTo(Number(t.dataset.index))));
  }
  const showVariantMedia = (mid) => {
    const i = slides.findIndex((sl) => sl.dataset.mediaId === String(mid));
    if (i > -1) goTo(i);
  };

  const paint = () => {
    const v = current();
    if (!v) return;
    prices.forEach((el) => (el.textContent = ils(packPrice(v))));
    if (colorName) colorName.textContent = state.color;
    if (colorShort) colorShort.textContent = state.color;
    swatches.forEach((s) => s.classList.toggle('on', s.dataset.color === state.color));
    bundles.forEach((b) => {
      const on = b.dataset.pack === state.pack;
      b.classList.toggle('on', on);
      const input = b.querySelector('input');
      if (input) input.checked = on;
    });
    buttons.forEach((btn) => (btn.disabled = !v.available));
    const packIndex = Number(bundles.find((b) => b.dataset.pack === state.pack)?.dataset.packIndex || 1);
    root.querySelectorAll('.gift[data-min-pack]').forEach((g) => g.classList.toggle('is-locked', packIndex < Number(g.dataset.minPack)));
  };

  swatches.forEach((s) =>
    s.addEventListener('click', () => {
      state.color = s.dataset.color;
      paint();
      showVariantMedia(current()?.mid);
    })
  );

  bundles.forEach((b) =>
    b.addEventListener('click', () => {
      state.pack = b.dataset.pack;
      paint();
    })
  );

  const ROOT = window.Shopify?.routes?.root || '/';
  const giftEls = () => [...root.querySelectorAll('.gift[data-gift-variant]')];
  const allGiftIds = () => giftEls().map((g) => Number(g.dataset.giftVariant));
  const perUnitGiftIds = () => giftEls().filter((g) => g.hasAttribute('data-per-unit')).map((g) => Number(g.dataset.giftVariant));
  const bonusGiftIds = () => giftEls().filter((g) => Number(g.dataset.minPack) > 1).map((g) => Number(g.dataset.giftVariant));
  const cartSectionIds = () => [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const getCart = async () => (await fetch(`${ROOT}cart.js`)).json();

  const addToCart = async () => {
    const v = current();
    if (!v) return;
    buttons.forEach((btn) => (btn.disabled = true));
    if (errorLine) errorLine.hidden = true;
    try {
      const cart = await getCart();
      const inCart = new Set(cart.items.map((i) => i.variant_id));
      // gifts that are unlocked for the chosen bundle go into the cart with the product, once per cart
      const gifts = giftEls()
        .filter((g) => !g.classList.contains('is-locked'))
        .map((g) => Number(g.dataset.giftVariant))
        .filter((id) => id && !inCart.has(id))
        .map((id) => ({ id, quantity: perUnitGiftIds().includes(id) ? packQty() : 1, properties: { _gift: '1' } }));
      const res = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [...gifts.slice().reverse(), { id: v.id, quantity: packQty() }], // Shopify puts each added line on top: send gifts first, product last, so the product shows first and the gifts under it
         sections: cartSectionIds().join(',') }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'add failed');
      document.dispatchEvent(
        new CartAddEvent({}, 'klr-lander', {
          source: 'product-form-component',
          itemCount: packQty() + gifts.length,
          productId: root.dataset.productId,
          variantId: String(v.id),
          sections: data.sections,
        })
      );
    } catch (err) {
      console.error(err);
      if (errorLine) {
        errorLine.textContent = 'משהו השתבש בהוספה לסל. נסו שוב.';
        errorLine.hidden = false;
      }
    } finally {
      paint();
    }
  };

  // Guard: gifts only live next to a real product. No product -> no gifts. Free home shipping gift only from 2 units (₪299+).
  let guarding = false;
  const guardCart = async () => {
    if (guarding || !allGiftIds().length) return;
    guarding = true;
    try {
      const cart = await getCart();
      const gifts = new Set(allGiftIds());
      const bonus = new Set(bonusGiftIds());
      const paid = cart.items.filter((i) => !gifts.has(i.variant_id));
      const paidTotal = paid.reduce((t, i) => t + i.final_line_price, 0);
      const paidQty = paid.reduce((t, i) => t + i.quantity, 0);
      const perUnit = new Set(perUnitGiftIds());
      const updates = {};
      cart.items.forEach((i) => {
        if (!gifts.has(i.variant_id)) return;
        // spare heads + travel pouch: one per device; guide + home shipping: always one. Never more, and only next to a paid product
        let q = paid.length ? (perUnit.has(i.variant_id) ? paidQty : 1) : 0;
        if (bonus.has(i.variant_id) && paidTotal < 29900) q = 0;
        if (i.quantity !== q) updates[i.key] = q;
      });
      // the free-home-shipping gift joins as soon as the order reaches 2 units (₪299+), e.g. "+" in the cart or a second add
      const inCart = new Set(cart.items.map((i) => i.variant_id));
      const missingBonus = paidTotal >= 29900 ? [...bonus].filter((id) => !inCart.has(id)) : [];
      let data = null;
      const post = async (url, body) => (await fetch(`${ROOT}${url}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })).json();
      const busy = (on) => document.querySelectorAll('cart-items-component').forEach((el) => el.classList.toggle('klr-busy', on));
      if (missingBonus.length) {
        // one write: gift quantities + take the paid lines out; second write: add the new gift, then the paid lines last.
        // (Shopify puts each added line on top, so the product stays first and the gifts under it.)
        busy(true);
        paid.forEach((i) => (updates[i.key] = 0));
        await post('cart/update.js', { updates });
        const items = [
          ...missingBonus.map((id) => ({ id, quantity: 1, properties: { _gift: '1' } })),
          ...paid.slice().reverse().map((i) => ({ id: i.variant_id, quantity: i.quantity, properties: i.properties || {} })),
        ];
        data = await post('cart/add.js', { items, sections: cartSectionIds().join(',') });
        const giftQty = cart.items.filter((i) => gifts.has(i.variant_id)).reduce((t, i) => t + (i.key in updates ? updates[i.key] : i.quantity), 0);
        data.item_count = paidQty + giftQty + missingBonus.length;
      } else if (Object.keys(updates).length) {
        busy(true);
        data = await post('cart/update.js', { updates, sections: cartSectionIds().join(',') });
      }
      busy(false);
      if (data) {
        document.dispatchEvent(new CartAddEvent({}, 'klr-guard', { source: 'klr-guard', itemCount: data.item_count, sections: data.sections }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => (guarding = false), 400);
    }
  };
  document.addEventListener(ThemeEvents.cartUpdate, (e) => {
    if (e.detail?.data?.source === 'klr-guard') return;
    setTimeout(guardCart, 0);
  });

  buttons.forEach((btn) => btn.addEventListener('click', addToCart));
  document.querySelectorAll('[data-klr-scroll-buy]').forEach((a) =>
    a.addEventListener('click', (e) => {
      e.preventDefault();
      root.scrollIntoView({ behavior: 'smooth' });
    })
  );
  paint();
}
